import { getFileExtensionFromUrl } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';
import { FileUploader } from '@/utils/uploader';

import { getFFmpegInstance } from './index';

export const cutMusic = async (
  url: string,
  maxDuration: number,
): Promise<{ url: string; duration: number }> => {
  try {
    // 1. 先下载音频文件并检查时长
    const response = await fetch(url);
    const blob = await response.blob();

    // 2. 使用 Audio API 获取音频时长
    const audioUrl = URL.createObjectURL(blob);
    const audio = new Audio(audioUrl);

    // 等待音频元数据加载
    const duration = await new Promise<number>((resolve, reject) => {
      audio.addEventListener('loadedmetadata', () => {
        resolve(audio.duration);
      });
      audio.addEventListener('error', () => {
        reject(new Error('Failed to load audio metadata'));
      });
    });

    // 清理临时 URL
    URL.revokeObjectURL(audioUrl);

    // 3. 如果时长不超过 maxDuration，直接返回原始 URL
    if (duration <= maxDuration) {
      return { url, duration };
    }

    // 4. 时长超过限制，加载 FFmpeg 进行裁剪
    const ffmpeg = await getFFmpegInstance();

    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // 5. 获取文件扩展名
    const ext = getFileExtensionFromUrl(url) || 'mp3';
    const inputFileName = `input.${ext}`;
    const outputFileName = `output.mp3`;

    // 6. 写入 FFmpeg 虚拟文件系统
    await ffmpeg.writeFile(inputFileName, uint8Array);

    // 7. 裁剪音频：取前 maxDuration 秒
    await ffmpeg.exec([
      '-i',
      inputFileName,
      '-t',
      maxDuration.toString(),
      '-acodec',
      'libmp3lame',
      '-ab',
      '128k', // 比特率
      '-ar',
      '24000', //采样率
      '-ac',
      '2', // 立体声
      '-y',
      outputFileName,
    ]);

    // 8. 读取裁剪后的文件
    const data = await ffmpeg.readFile(outputFileName);
    // @ts-ignore
    const outputBlob = new Blob([data], { type: `audio/mpeg` });
    const outputFile = new File([outputBlob], outputFileName, { type: `audio/mpeg` });

    // 9. 清理虚拟文件系统
    try {
      await ffmpeg.deleteFile(inputFileName);
      await ffmpeg.deleteFile(outputFileName);
    } catch (e) {
      console.warn('Failed to cleanup FFmpeg files:', e);
    }

    // 10. 上传裁剪后的文件
    const uploadedUrl = await uploadFile(outputFile);
    return { url: uploadedUrl, duration: maxDuration };
  } catch (error) {
    throw {
      message: t('Trim-failed'),
    };
  }
};

export const uploadFile = (file: File): Promise<string> => {
  return new Promise<string>(async (resolve) => {
    const uploader = FileUploader.getInstance({
      onSuccess(url: string) {
        resolve(url);
      },
    });
    await uploader.upload({
      file: file,
      tokenData: {
        fileName: file.name,
        dirSceneType: 20,
      },
    });
  });
};
